import lib from '../../assets/utils/lib';

export const ORG_PAYMENT_ADVICE_EXPORT_TYPE = 'org.payment_advice';

export const ORG_PAYMENT_ADVICE_PERMISSIONS = [
  'admin.*',
  'organizations.reports',
  'claims.payment',
  'claims.reports',
  'corporations.view_reports',
];

export const DATA_SCOPE_CLAIMS = 'claims_payment';
export const DATA_SCOPE_PA = 'pa_approved';

export const DATA_SCOPE_OPTIONS = [
  { value: DATA_SCOPE_CLAIMS, label: 'Paid / pending payment (claims)' },
  { value: DATA_SCOPE_PA, label: 'PA approvals (may not be claimed yet)' },
];

export const PA_LINE_STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'queried', label: 'Queried' },
  { value: 'rejected', label: 'Rejected' },
];

export const OUTPUT_FORMAT_OPTIONS = [
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'pdf', label: 'PDF (.pdf)' },
  { value: 'json', label: 'Raw data (advanced)' },
];

export const DEFAULT_REPORT_OPTIONS = {
  data_scope: DATA_SCOPE_CLAIMS,
  total_approved_pa_metric: 'pa',
  period_anchor: 'check_in',
  category_approved_cost_metric: 'claims',
  pa_line_statuses: ['approved'],
};

export const DEFAULT_EXPORT_PARAMS = {
  report_variant: 'payment_advice.med',
  output_format: 'xlsx',
  claim_segment: 3,
  grouping: 'encounter_with_category_lines',
  filters: {
    plan_type: [],
    policy_id: [],
    facility_id: [],
    request_category_id: [],
    search: '',
  },
  report_options: { ...DEFAULT_REPORT_OPTIONS },
};

export function canExportOrgPaymentAdvice(user) {
  const perms = user?.profile?.permissions;
  if (!Array.isArray(perms) || perms.length === 0) return true;
  return perms.some((p) => ORG_PAYMENT_ADVICE_PERMISSIONS.includes(p));
}

export function isPaApprovedScope(reportOptions = {}) {
  return reportOptions?.data_scope === DATA_SCOPE_PA;
}

export function getOrgJobOutputFormat(job) {
  return (
    job?.report_metadata?.output_format ||
    job?.params?.output_format ||
    'json'
  );
}

export function isServerRenderedOutputFormat(format) {
  return format === 'xlsx' || format === 'pdf';
}

export function parseReportOptionsFromSchema(schema) {
  const defs = schema?.report_options;
  const out = { ...DEFAULT_REPORT_OPTIONS };
  if (!Array.isArray(defs)) {
    return out;
  }
  defs.forEach((field) => {
    if (field?.key && field.default !== undefined) {
      out[field.key] = field.default;
    }
  });
  if (!Array.isArray(out.pa_line_statuses) || !out.pa_line_statuses.length) {
    out.pa_line_statuses = ['approved'];
  }
  return out;
}

export function buildOrgPaymentAdviceParams({
  organizationId,
  organizationIds,
  period,
  claimSegment = 3,
  filters = {},
  reportOptions = DEFAULT_REPORT_OPTIONS,
  reportVariant = 'payment_advice.med',
  grouping = 'encounter_with_category_lines',
  columns,
  outputFormat = 'xlsx',
}) {
  if (!period?.type) throw new Error('period.type is required');

  const mergedOptions = { ...DEFAULT_REPORT_OPTIONS, ...reportOptions };
  if (!Array.isArray(mergedOptions.pa_line_statuses) || !mergedOptions.pa_line_statuses.length) {
    mergedOptions.pa_line_statuses = ['approved'];
  }

  const hasMultiOrg = Array.isArray(organizationIds) && organizationIds.length > 0;
  if (!hasMultiOrg && !organizationId) {
    throw new Error('organization_id or organization_ids is required');
  }

  const params = {
    report_variant: reportVariant,
    output_format: outputFormat,
    period: { ...period },
    grouping,
    filters: {
      plan_type: [],
      policy_id: [],
      facility_id: [],
      request_category_id: [],
      search: '',
      ...filters,
    },
    report_options: mergedOptions,
  };

  if (hasMultiOrg) {
    params.organization_ids = organizationIds;
  } else {
    params.organization_id = organizationId;
  }

  if (!isPaApprovedScope(mergedOptions)) {
    params.claim_segment = claimSegment;
  }

  if (Array.isArray(columns) && columns.length) {
    params.columns = columns;
  }

  return params;
}

export function formatMonthsLabel(months = []) {
  if (!Array.isArray(months) || !months.length) return '—';
  if (months.length === 1) return months[0];
  return `${months[0]} – ${months[months.length - 1]}`;
}

export function formatCurrencyNgn(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

export function orgPaymentAdviceFilename(payload, ext) {
  const isMulti = payload?.scope_mode === 'multi';
  const orgSlug = isMulti
    ? 'Multi_Org'
    : String(payload?.organization?.name || 'organization')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 40);
  const scope = payload?.scope || {};
  let periodPart = 'export';
  if (scope.period_type === 'month' && scope.month) {
    periodPart = scope.month;
  } else if (scope.month_from && scope.month_to) {
    periodPart = `${scope.month_from}_${scope.month_to}`;
  } else if (scope.date_from && scope.date_to) {
    periodPart = `${scope.date_from}_${scope.date_to}`;
  } else if (scope.payment_batch) {
    periodPart = String(scope.payment_batch).replace(/[^a-zA-Z0-9_-]+/g, '_');
  }
  return `CBA_Payment_Advice_${orgSlug}_${periodPart}.${ext}`;
}

export async function previewOrgPaymentAdvice(params) {
  const response = await lib.previewOrgPaymentAdvice(params);
  if (response?.data?.error) {
    throw new Error(response.data.message || 'Preview failed');
  }
  return response?.data?.data;
}

export async function queueOrgPaymentAdviceExport(params) {
  const response = await lib.queueOrgPaymentAdviceExport(params);
  if (response?.data?.error) {
    throw new Error(response.data.message || 'Unable to queue export job');
  }
  return response;
}

export async function fetchOrgPaymentAdviceDataSync(params) {
  const outputFormat = params?.output_format || 'json';
  if (isServerRenderedOutputFormat(outputFormat)) {
    return lib.downloadOrgPaymentAdviceSync(params);
  }
  const response = await lib.fetchOrgPaymentAdviceData(params);
  if (response?.data?.error) {
    throw new Error(response.data.message || 'Failed to load payment advice data');
  }
  return response?.data?.data;
}

export function normalizeOrgPaymentAdvicePayload(payload) {
  if (payload?.scope_mode !== 'multi' || !Array.isArray(payload?.organization_sections)) {
    return payload;
  }

  const rows = [];
  const rows_flat = [];

  payload.organization_sections.forEach((section) => {
    const orgName =
      section.organization?.name ||
      payload.organizations?.find((o) => o.organization_id === section.scope?.organization_id)?.name ||
      `Org ${section.scope?.organization_id ?? ''}`;

    (section.rows || []).forEach((row) => {
      rows.push({ ...row, organization_name: orgName });
    });

    (section.rows_flat || []).forEach((row) => {
      rows_flat.push({ ...row, organization_name: orgName });
    });
  });

  return {
    ...payload,
    organization: { name: 'Multiple organizations' },
    summary: payload.combined_summary || payload.summary,
    rows,
    rows_flat,
  };
}
